import React, {createContext, useCallback, useContext, useMemo} from "react";
import {camelCaseToWords} from "@me8eon/string_utils";
import {makeContextForState} from "@me8eon/context";
import i18nOriginal from "i18next";

export type RawTranslationFn = ( key: string) => string | undefined;
export type TranslationFn = (key: string) => string;

export type TranslationI18n = {
    language: string;
    changeLanguage: (language: string) => void;
};
export type UseTranslationResult = TranslationFn & {
    t: TranslationFn;
    i18n: TranslationI18n;
};

// Default function to transform keys into readable labels
export const defaultTranslationFn: TranslationFn = camelCaseToWords;

export function emptyUsedAndNotFound(): UsedAndNotFound {
    return {used: new Set(), notFound: new Set(), errors: new Set()};
}

export type UsedAndNotFound = {
    used: Set<string>;
    notFound: Set<string>;
    errors: Set<string>;
};

// Create context for tracking translation usage and errors
export const {use: useTranslationUsedAndNotFound, Provider: TranslationUsedAndNotFoundProvider} =
    makeContextForState<UsedAndNotFound, "usedAndNotFound">("usedAndNotFound", {allowedUndefined:true});

// Context to provide the translation function
export const TranslationContext = createContext<TranslationFn>(defaultTranslationFn);

export type TranslationProviderProps = {
    translationFn: RawTranslationFn;
    defaultFn?: TranslationFn;
    children: React.ReactNode;
};

// Default function for missing translations
export function NotFoundTranslationFn(key: string): string {
    return `${key}.not.found`;
}

// Reusable function to update translation tracking state
function updateSetInState(
    key: string,
    type: keyof UsedAndNotFound,
    setUsedAnd: (updateFn: (prev: UsedAndNotFound) => UsedAndNotFound) => void
) {
    setTimeout(() => {
        setUsedAnd((prev) => {
            const newSet = new Set(prev[type]);
            newSet.add(key);
            const result = {...prev, [type]: newSet};
            return result;
        });
    }, 0);
}

export function TranslationProvider({
                                        translationFn,
                                        children,
                                        defaultFn = NotFoundTranslationFn,
                                    }: TranslationProviderProps) {
    const ops = useTranslationUsedAndNotFound();

    const translation: TranslationFn = useCallback(
        (key: string) => {
            const result = translationFn( key);

            if (ops) {
                const [usedAnd, setUsedAnd] = ops;

                if (result === undefined) {
                    if (!usedAnd.notFound.has(key)) {
                        updateSetInState(key, "notFound", setUsedAnd);
                    }
                } else {
                    if (!usedAnd.used.has(key)) {
                        updateSetInState(key, "used", setUsedAnd);
                    }
                    if (typeof result !== "string" && !usedAnd.errors.has(key)) {
                        updateSetInState(key, "errors", setUsedAnd);
                    }
                }
            }

            // Return the result or default fallback if translation not found
            return result === undefined
                ? defaultFn(key)
                : typeof result === "string"
                    ? result
                    : `${key}.error`;
        },
        [translationFn, defaultFn, ops]
    );

    return <TranslationContext.Provider value={translation}>{children}</TranslationContext.Provider>;
}

// Hook to consume the translation function
export function useTranslation(filename = "translation"): UseTranslationResult {
    const translation = useContext(TranslationContext);

    const scopedTranslation = useCallback<TranslationFn>((key: string) => {
        const namespacedKey = `${filename}.${key}`;

        const isMissingKey = (resolvedValue: string, resolvedKey: string) => {
            return resolvedValue === `${resolvedKey}.not.found` || resolvedValue === `${resolvedKey}.error`;
        };

        if (filename === "translation") {
            return translation(key);
        }

        const namespacedValue = translation(namespacedKey);
        if (!isMissingKey(namespacedValue, namespacedKey)) {
            return namespacedValue;
        }
        return translation(key);
    }, [filename, translation]);

    const language = typeof localStorage === "undefined"
        ? "en"
        : (localStorage.getItem("i18nextLng") || "en").slice(0, 2);

    const i18n = useMemo<TranslationI18n>(() => {

        return {
            language,
            changeLanguage: (nextLanguage: string) => {
                if (typeof localStorage !== "undefined") {
                    i18nOriginal.changeLanguage(nextLanguage);
                    localStorage.setItem("i18nextLng", nextLanguage);
                }
            }
        };
    }, [language]);

    return useMemo<UseTranslationResult>(() => {
        const translationWithMetadata = ((key: string) => scopedTranslation(key)) as UseTranslationResult;
        translationWithMetadata.t = scopedTranslation;
        translationWithMetadata.i18n = i18n;
        return translationWithMetadata;
    }, [scopedTranslation, i18n]);
}
